/**
 * Onyx delivery promise countdown.
 *
 * The line is already rendered by Liquid, so this module only refines it:
 * it recomputes the Europe/London time in the browser (correct even on a
 * cached page, and for visitors in any timezone) and ticks once a minute.
 *
 * No scarcity, no invented deadlines: the only deadline is the published
 * same-day dispatch cut-off on working days.
 */

const SELECTOR = '[data-onyx-delivery-promise]';
const TIME_ZONE = 'Europe/London';
const TICK_MS = 30000;

/** @type {Intl.DateTimeFormat | null} */
let londonFormatter = null;

/**
 * @returns {Intl.DateTimeFormat | null} A formatter locked to Europe/London, or null when unsupported.
 */
function getFormatter() {
  if (londonFormatter) return londonFormatter;

  try {
    londonFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hourCycle: 'h23',
    });
  } catch (_) {
    londonFormatter = null;
  }

  return londonFormatter;
}

/**
 * Current London time as parts.
 * @returns {{hour: number, minute: number, weekday: string} | null}
 */
function londonNow() {
  const formatter = getFormatter();
  if (!formatter) return null;

  /** @type {Record<string, string>} */
  const parts = {};
  for (const part of formatter.formatToParts(new Date())) {
    parts[part.type] = part.value;
  }

  const hour = Number.parseInt(parts.hour ?? '', 10);
  const minute = Number.parseInt(parts.minute ?? '', 10);
  const weekday = parts.weekday ?? '';

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  return { hour, minute, weekday };
}

/**
 * @param {number} minutes - Whole minutes remaining.
 * @returns {string} e.g. "2h 35m" or "45m".
 */
function formatRemaining(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
}

/** @param {HTMLElement} element */
function update(element) {
  const message = element.querySelector('[data-onyx-delivery-promise-message]');
  if (!(message instanceof HTMLElement)) return;

  const now = londonNow();
  if (!now) return;

  const cutoffHour = Number.parseInt(element.dataset.cutoffHour ?? '16', 10);
  if (!Number.isFinite(cutoffHour)) return;

  const isWorkingDay = !['Sat', 'Sun'].includes(now.weekday.slice(0, 3));
  const minutesLeft = cutoffHour * 60 - (now.hour * 60 + now.minute);
  const nextDayText = element.dataset.nextDayText ?? 'Order now for dispatch on the next working day';

  if (!isWorkingDay || minutesLeft <= 0) {
    if (message.textContent?.trim() !== nextDayText) message.textContent = nextDayText;
    return;
  }

  const template = element.dataset.sameDayTemplate ?? 'Order in the next [time] for same-day dispatch';
  const next = template.replace('[time]', formatRemaining(minutesLeft));

  if (message.textContent?.trim() !== next) message.textContent = next;
}

function updateAll() {
  for (const element of document.querySelectorAll(SELECTOR)) {
    if (element instanceof HTMLElement) update(element);
  }
}

updateAll();
setInterval(updateAll, TICK_MS);
document.addEventListener('shopify:section:load', updateAll);
